// =============================================================================
// RoleTable
//
// Displays roles in a sortable, filterable table with clickable rows.
// Per AC: no action icons, no pagination — browser-native vertical scrolling.
// Column header filter icons open inline dropdowns.
// Status shown as colored tag (toggle is in the edit modal only).
// =============================================================================

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  StyleSheet,
  Platform,
  Dimensions,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import LHDataTableNoPagination from './LHDataTableNoPagination';
import FontFamily from '../../../../../constants/fonts';
import { useTheme } from '../../../../../theme/useTheme';
import { Role, RoleFilters, RoleTableProps } from '../types';

// =============================================================================
// CONSTANTS — mirrors the HTML reference filter options
// =============================================================================

const LOC_TYPE_OPTIONS = ['Distribution', 'Production', 'Manufacturing'];
const AVAILABILITY_OPTIONS = ['Observations', 'Labor Optimization'];
const ORG_TYPE_OPTIONS = ['PBNA', 'FLNA', '1NA'];
const THREE_PL_OPTIONS = ['Yes', 'No', 'Any'];
const ROLE_TYPE_OPTIONS = ['DSD', 'Non-DSD', 'Fixed'];
const STATUS_OPTIONS = ['Enabled', 'Disabled'];

// =============================================================================
// SMALL HELPER COMPONENTS
// =============================================================================

/** Coloured tag chip — matches the design system. */
const Tag = ({ label, variant }: { label: string; variant: 'blue' | 'purple' | 'teal' | 'green' | 'gray' | 'red' }) => {
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === 'dark';
  const colors: Record<string, { bg: string; text: string }> = isDarkTheme ? {
    blue:   { bg: '#1A5BB5', text: '#FFFFFF' },
    purple: { bg: '#4A3FA0', text: '#FFFFFF' },
    teal:   { bg: '#1E5A7A', text: '#FFFFFF' },
    green:  { bg: '#1A6B24', text: '#FFFFFF' },
    gray:   { bg: '#4B4B4B', text: '#FFFFFF' },
    red:    { bg: '#9B2626', text: '#FFFFFF' },
  } : {
    blue:   { bg: '#DAEEFA', text: '#01346F' },
    purple: { bg: '#E1E3FD', text: '#31217D' },
    teal:   { bg: '#AAD2E2', text: '#1E5A7A' },
    green:  { bg: '#CAF3C6', text: '#0C420B' },
    gray:   { bg: '#E6E6E6', text: '#4B4B4B' },
    red:    { bg: '#FDDEDE', text: '#A80000' },
  };
  const c = colors[variant];
  return (
    <View style={[styles.tag, { backgroundColor: c.bg }]}>
      <Text style={[styles.tagText, { color: c.text }]}>{label}</Text>
    </View>
  );
};

/* StatusToggle, EditIconButton, DeleteIconButton removed — AC: no action icons, row click only */

/** Filter dropdown chip — uses Modal (same pattern as FilterDropdown.tsx)
 *  so the dropdown renders at the React root and receives all pointer events. */
const FilterChip = ({
  label,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string;
  options: readonly string[];
  selected: Set<string>;
  onToggle: (opt: string) => void;
  onClear: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const btnRef = useRef<any>(null);
  const isActive = selected.size > 0;

  const updatePos = useCallback(() => {
    if (!btnRef.current) return;
    if (Platform.OS === 'web') {
      try {
        const rect = (btnRef.current as any).getBoundingClientRect();
        const screenW = Dimensions.get('window').width;
        const dropW = Math.max(rect.width + 24, 200);
        let left = rect.left;
        if (left + dropW > screenW - 8) left = Math.max(8, screenW - dropW - 8);
        setPos({ top: rect.bottom + 4, left, width: dropW });
      } catch { /* ignore */ }
    }
  }, []);

  // Keep dropdown anchored to button while page scrolls
  useEffect(() => {
    if (!open || Platform.OS !== 'web') return;
    const win = window as any;
    win.addEventListener('scroll', updatePos, true);
    return () => win.removeEventListener('scroll', updatePos, true);
  }, [open, updatePos]);

  const handleOpen = () => {
    if (!btnRef.current) { setOpen(true); return; }
    if (Platform.OS === 'web') {
      updatePos();
    } else {
      try {
        (btnRef.current as any).measureInWindow((x: number, y: number, w: number, h: number) => {
          const screenW = Dimensions.get('window').width;
          const dropW = Math.max(w + 24, 200);
          let left = x;
          if (left + dropW > screenW - 8) left = Math.max(8, screenW - dropW - 8);
          setPos({ top: y + h + 4, left, width: dropW });
        });
      } catch { /* fallback position */ }
    }
    setOpen(true);
  };

  return (
    <>
      <TouchableOpacity
        ref={btnRef}
        onPress={handleOpen}
        activeOpacity={0.7}
        style={[styles.filterChip, isActive && styles.filterChipActive]}
      >
        <Text style={[styles.filterChipText, isActive && styles.filterChipTextActive]}>
          {label}{isActive ? ` (${selected.size})` : ''}
        </Text>
        <Text style={[styles.filterChipArrow, isActive && styles.filterChipArrowActive]}>▾</Text>
      </TouchableOpacity>

      {open && (
        <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setOpen(false)}
          >
            <View
              style={[
                styles.dropdownMenu,
                { position: 'absolute', top: pos.top, left: pos.left, minWidth: pos.width },
              ]}
              onStartShouldSetResponder={() => true}
            >
              <ScrollView
                style={styles.dropdownScroll}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={options.length > 6}
              >
                {options.map((opt) => (
                  <TouchableOpacity
                    key={opt}
                    onPress={() => onToggle(opt)}
                    activeOpacity={0.7}
                    style={styles.dropdownItem}
                  >
                    <View style={[styles.checkbox, selected.has(opt) && styles.checkboxChecked]}>
                      {selected.has(opt) && (
                        <Svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}>
                          <Path d="M20 6L9 17l-5-5" />
                        </Svg>
                      )}
                    </View>
                    <Text style={styles.dropdownItemText}>{opt}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>

              <View style={styles.dropdownFooter}>
                <TouchableOpacity onPress={() => { onClear(); setOpen(false); }}>
                  <Text style={styles.dropdownClearText}>Clear</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        </Modal>
      )}
    </>
  );
};

/** Loading skeleton rows shown while isLoading = true. */
const SkeletonRow = () => (
  <View style={styles.skeletonRow}>
    {[40, 120, 100, 80, 60].map((w, i) => (
      <View key={i} style={[styles.skeletonCell, { width: w }]} />
    ))}
  </View>
);

/** Empty state shown when no rows match filters. */
const EmptyState = ({ hasFilters, onClear }: { hasFilters: boolean; onClear: () => void }) => (
  <View style={styles.emptyState}>
    <Svg width={40} height={40} viewBox="0 0 24 24" fill="none" stroke="#C0C0C0" strokeWidth={1.5}>
      <Path d="M9 11l3 3L22 4" />
      <Path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </Svg>
    <Text style={styles.emptyTitle}>No Roles match your current filters and search criteria</Text>
    {hasFilters && (
      <TouchableOpacity onPress={onClear}>
        <Text style={styles.emptyLink}>Clear Filters</Text>
      </TouchableOpacity>
    )}
  </View>
);

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const RoleTable: React.FC<RoleTableProps> = ({ 
  roles, 
  isLoading, 
  pagination,
  sort,
  filterOptions,
  onRowClick,
  onApplyFilters,
  onSortChange,
  onReset,
}) => {
  // Derive all filter options from the actual roles data so values always
  // match exactly (avoids mismatch between API filterOptions and role fields).
  const locationTypeOptions = useMemo(
    () => Array.from(new Set(roles.flatMap((r) => r.locTypes))).sort(),
    [roles],
  );
  const availableInOptions = useMemo(
    () => Array.from(new Set(roles.flatMap((r) => r.availableIn))).sort(),
    [roles],
  );
  const statusOptions = useMemo(
    () => Array.from(new Set(roles.map((r) => r.status))).sort(),
    [roles],
  );
  const organizationTypeOptions = useMemo(
    () => Array.from(new Set(roles.flatMap((r) => r.organizationTypes ?? []))).sort(),
    [roles],
  );
  const threePlStatusOptions = useMemo(
    () => Array.from(new Set(roles.map((r) => r.threePlStatus).filter(Boolean) as string[])).sort(),
    [roles],
  );
  const roleTypeOptions = useMemo(
    () => Array.from(new Set(roles.map((r) => r.roleType).filter(Boolean) as string[])).sort(),
    [roles],
  );

  // ── Search (reactive — instant filter, no Apply needed) ────────────────────
  const [searchQuery, setSearchQuery] = useState('');

  // ── Filters (auto-applied on selection, no Apply button needed) ────────────
  // Since there is no pagination and all roles are fetched at once,
  // ALL filtering is done client-side for instant results.
  const [filters, setFilters] = useState<RoleFilters>({});

  const roleNameOptions = useMemo(
    () => Array.from(new Set(roles.map((r) => r.name))).sort(),
    [roles],
  );

  // Toggle a single value within a filter column.
  // Uses functional setState so it always reads the latest state,
  // avoiding stale-closure issues with React batched updates.
  const handleFilterToggle = useCallback((key: keyof RoleFilters, opt: string) => {
    setFilters((prev) => {
      const updated = { ...prev };
      const current = new Set(prev[key] ?? []);
      if (current.has(opt)) current.delete(opt);
      else current.add(opt);
      if (current.size === 0) delete updated[key];
      else updated[key] = current;
      return updated;
    });
  }, []);

  // Clear all selections for a single filter column.
  const handleFilterClear = useCallback((key: keyof RoleFilters) => {
    setFilters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  }, []);

  const resetAll = () => {
    setSearchQuery('');
    setFilters({});
    onReset();
  };

  const hasAppliedFilters =
    searchQuery.trim().length > 0 ||
    Object.values(filters).some((s) => s && s.size > 0);

  // ── All filtering done client-side on the full roles list ─────────────────
  const filteredRoles = useMemo(() => {
    let result = roles;
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((role) => role.name.toLowerCase().includes(q));
    }
    const nameFilter = filters.name;
    if (nameFilter && nameFilter.size > 0) {
      result = result.filter((role) => nameFilter.has(role.name));
    }
    const availFilter = filters.availableIn;
    if (availFilter && availFilter.size > 0) {
      result = result.filter((role) => role.availableIn.some((a) => availFilter.has(a)));
    }
    const locFilter = filters.locTypes;
    if (locFilter && locFilter.size > 0) {
      result = result.filter((role) => role.locTypes.some((l) => locFilter.has(l)));
    }
    const statusFilter = filters.status;
    if (statusFilter && statusFilter.size > 0) {
      result = result.filter((role) => statusFilter.has(role.status));
    }
    const orgFilter = filters.organizationTypes;
    if (orgFilter && orgFilter.size > 0) {
      result = result.filter((role) => (role.organizationTypes ?? []).some((o) => orgFilter.has(o)));
    }
    const tplFilter = filters.threePlStatus;
    if (tplFilter && tplFilter.size > 0) {
      result = result.filter((role) => role.threePlStatus ? tplFilter.has(role.threePlStatus) : false);
    }
    const rtFilter = filters.roleType;
    if (rtFilter && rtFilter.size > 0) {
      result = result.filter((role) => role.roleType ? rtFilter.has(role.roleType) : false);
    }
    return result;
  }, [roles, searchQuery, filters]);

  // ── Column definitions for LHDataTable ────────────────────────────────────
  // renderCell is used for all visual columns (tags, toggle, actions).
  // LHDataTable handles sorting via the header tap on the Role Name column.
  const columns = useMemo(() => [
    {
      columnKey: 'name',
      header: 'Role Name',
      flexGrow: 2,
    },
    {
      columnKey: 'availableIn',
      header: 'Available In',
      flexGrow: 2,
      renderCell: (item: Role) => (
        <View style={styles.tagRow}>
          {item.availableIn.map((a) => (
            <Tag
              key={a}
              label={a === 'Labor Optimization' ? 'Labor Opt.' : a}
              variant={a === 'Observations' ? 'purple' : 'green'}
            />
          ))}
        </View>
      ),
    },
    {
      columnKey: 'roleType',
      header: 'Role Type',
      width: 130,
      renderCell: (item: Role) => {
        const hasLO = item.availableIn.includes('Labor Optimization');
        if (!hasLO) {
          return <Text style={styles.mutedDash}>—</Text>;
        }
        return (
          <View style={styles.tagRow}>
            {item.roleType ? <Tag label={item.roleType} variant="teal" /> : null}
          </View>
        );
      },
    },
    {
      columnKey: 'locTypes',
      header: 'Location Type(s)',
      flexGrow: 2,
      renderCell: (item: Role) => (
        <View style={styles.tagRow}>
          {item.locTypes.map((l) => (
            <Tag key={l} label={l} variant="teal" />
          ))}
        </View>
      ),
    },
    {
      columnKey: 'organizationTypes',
      header: 'Org Type(s)',
      flexGrow: 2,
      renderCell: (item: Role) => (
        <View style={styles.tagRow}>
          {(item.organizationTypes ?? []).map((org) => (
            <Tag key={org} label={org} variant="blue" />
          ))}
        </View>
      ),
    },
    {
      columnKey: 'threePlStatus',
      header: '3PL Status',
      width: 100,
      renderCell: (item: Role) => (
        <View style={styles.tagRow}>
          {item.threePlStatus ? <Text style={styles.plainText}>{item.threePlStatus}</Text> : null}
        </View>
      ),
    },
    {
      columnKey: 'status',
      header: 'Status',
      width: 120,
      renderCell: (item: Role) => {
        const isEnabled = item.status === 'Enabled';
        return (
          <Tag
            label={isEnabled ? 'Enabled' : 'Disabled'}
            variant={isEnabled ? 'green' : 'gray'}
          />
        );
      },
    },
  ], []);

  // ── Summary label ──────────────────────────────────────────────────────────
  const summaryLabel = isLoading
    ? 'Loading...'
    : `Showing ${filteredRoles.length} of ${roles.length} Roles`;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <View style={styles.wrapper}>

      {/* ── Top bar: search + column filters + clear ── */}
      <View style={styles.topBar}>
        <View style={styles.topBarLeft}>
          {/* Search input */}
          <View style={styles.searchWrapper}>
            <Svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="#818181" strokeWidth={2} style={styles.searchIcon}>
              <Path d="M21 21l-4.35-4.35" />
              <Path d="M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z" />
            </Svg>
            <TextInput
              style={styles.searchInput}
              placeholder="Search roles..."
              placeholderTextColor="#838383"
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>

          {/* Filter chips — auto-applied on selection */}
          <FilterChip
            label="Role Name"
            options={roleNameOptions}
            selected={filters.name ?? new Set()}
            onToggle={(opt) => handleFilterToggle('name', opt)}
            onClear={() => handleFilterClear('name')}
          />
          <FilterChip
            label="Available In"
            options={availableInOptions}
            selected={filters.availableIn ?? new Set()}
            onToggle={(opt) => handleFilterToggle('availableIn', opt)}
            onClear={() => handleFilterClear('availableIn')}
          />
          <FilterChip
            label="Location Type"
            options={locationTypeOptions}
            selected={filters.locTypes ?? new Set()}
            onToggle={(opt) => handleFilterToggle('locTypes', opt)}
            onClear={() => handleFilterClear('locTypes')}
          />
          <FilterChip
            label="Status"
            options={statusOptions}
            selected={filters.status ?? new Set()}
            onToggle={(opt) => handleFilterToggle('status', opt)}
            onClear={() => handleFilterClear('status')}
          />
          <FilterChip
            label="Organization Type"
            options={organizationTypeOptions}
            selected={filters.organizationTypes ?? new Set()}
            onToggle={(opt) => handleFilterToggle('organizationTypes', opt)}
            onClear={() => handleFilterClear('organizationTypes')}
          />
          <FilterChip
            label="3PL"
            options={threePlStatusOptions}
            selected={filters.threePlStatus ?? new Set()}
            onToggle={(opt) => handleFilterToggle('threePlStatus', opt)}
            onClear={() => handleFilterClear('threePlStatus')}
          />
          <FilterChip
            label="Role Type"
            options={roleTypeOptions}
            selected={filters.roleType ?? new Set()}
            onToggle={(opt) => handleFilterToggle('roleType', opt)}
            onClear={() => handleFilterClear('roleType')}
          />

          {/* Reset all — visible when any filter is active */}
          {hasAppliedFilters && (
            <TouchableOpacity onPress={resetAll} style={styles.resetAllBtn} activeOpacity={0.8}>
              <Svg width={13} height={18} viewBox="0 0 14 19" fill="none">
                <Path d="M6.66667 2.5V0L3.33333 3.33333L6.66667 6.66667V4.16667C9.425 4.16667 11.6667 6.40833 11.6667 9.16667C11.6667 10.0083 11.4583 10.8083 11.0833 11.5L12.3 12.7167C12.95 11.6917 13.3333 10.475 13.3333 9.16667C13.3333 5.48333 10.35 2.5 6.66667 2.5ZM6.66667 14.1667C3.90833 14.1667 1.66667 11.925 1.66667 9.16667C1.66667 8.325 1.875 7.525 2.25 6.83333L1.03333 5.61667C0.383333 6.64167 0 7.85833 0 9.16667C0 12.85 2.98333 15.8333 6.66667 15.8333V18.3333L10 15L6.66667 11.6667V14.1667Z" fill="#005CBC"/>
              </Svg>
              <Text style={styles.resetAllText}>Reset all</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Summary count — right-aligned */}
        <Text style={styles.summary}>{summaryLabel}</Text>
      </View>

      {/* ── Loading skeleton ── */}
      {isLoading && (
        <View>
          {[1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} />)}
        </View>
      )}

      {/* ── Empty state ── */}
      {!isLoading && filteredRoles.length === 0 && (
        <EmptyState hasFilters={hasAppliedFilters} onClear={resetAll} />
      )}

      {/* ── LHDataTable — only rendered when there is data ── */}
      {!isLoading && filteredRoles.length > 0 && (
        <LHDataTableNoPagination
          data={filteredRoles}
          columns={columns}
          tableHeader=""
          tableDescription=""
          maxRecordsToShow={filteredRoles.length}
          showHeaderTopBorder={false}
          onRowClick={(item: Role) => onRowClick(item)}
        />
      )}
    </View>
  );
};

// =============================================================================
// STYLES
// Uses the same colour tokens as the HTML reference design system.
// =============================================================================

const styles = StyleSheet.create({
  wrapper: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    // Card shadow matching HTML --elev-01
    shadowColor: '#005EC0',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 2,
  },

  // ── Top bar ───────────────────────────────────────────────────────────────
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F3F3',
    flexWrap: 'wrap',
    gap: 6,
  },
  topBarLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
  },

  // ── Search ────────────────────────────────────────────────────────────────
  searchWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 38,
    borderWidth: 1,
    borderColor: '#D0D0D0',
    borderRadius: 6,
    paddingHorizontal: 10,
    backgroundColor: '#FFFFFF',
    minWidth: 220,
  },
  searchIcon: {
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    height: 36,
    fontSize: 14,
    color: '#2D2D2D',
    fontFamily: FontFamily.regular,
    paddingVertical: 0,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },

  // ── Filter chips — pill style matching FilterDropdown ────────────────────
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 32,
    paddingHorizontal: 16,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#D7E5F8',
    backgroundColor: '#FBFCFE',
  },
  filterChipActive: {
    borderColor: '#034895',
    backgroundColor: '#034895',
  },
  filterChipText: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#034895',
  },
  filterChipTextActive: { color: '#FFFFFF' },
  filterChipArrow: { fontSize: 12, color: '#034895' },
  filterChipArrowActive: { color: '#FFFFFF' },

  // ── Modal dropdown (renders at React root — fixes click issues) ───────────
  modalOverlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownMenu: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D7E5F8',
    borderRadius: 8,
    paddingVertical: 6,
    shadowColor: '#3A3A3A',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 12,
  },
  dropdownItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  dropdownItemText: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: '#2D2D2D',
  },
  dropdownScroll: {
    maxHeight: 240,
  },
  dropdownFooter: {
    borderTopWidth: 1,
    borderTopColor: '#F3F3F3',
    paddingHorizontal: 12,
    paddingTop: 6,
    paddingBottom: 2,
    marginTop: 4,
  },
  dropdownClearText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#005CBC',
  },

  // Checkbox inside filter dropdown
  checkbox: {
    width: 14,
    height: 14,
    borderWidth: 1.5,
    borderColor: '#C0C0C0',
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxChecked: {
    backgroundColor: '#005CBC',
    borderColor: '#005CBC',
  },

  // ── Reset all button ──────────────────────────────────────────────────────
  resetAllBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  resetAllText: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    color: '#005CBC',
  },

  // ── Summary label ─────────────────────────────────────────────────────────
  summary: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#616161',
  },

  // ── Tag chips ─────────────────────────────────────────────────────────────
  tagRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 3,
  },
  tag: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 1,
  },
  tagText: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
  },

  // ── Muted dash for Role Type when non-LO ─────────────────────────────────
  mutedDash: {
    fontSize: 14,
    color: '#A0A0A0',
    fontFamily: FontFamily.regular,
  },
  plainText: {
    fontSize: 13,
    color: '#3A3A3A',
    fontFamily: FontFamily.regular,
  },

  // ── Skeleton ──────────────────────────────────────────────────────────────
  skeletonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 48,
    paddingHorizontal: 14,
    gap: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F3F3',
  },
  skeletonCell: {
    height: 12,
    borderRadius: 6,
    backgroundColor: '#EBEBEB',
  },

  // ── Empty state ───────────────────────────────────────────────────────────
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 24,
    gap: 8,
  },
  emptyTitle: {
    fontSize: 15,
    fontFamily: FontFamily.medium,
    color: '#616161',
  },
  emptyLink: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#005CBC',
  },

});

export default RoleTable;
